import { useState } from 'react'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import type { ValidPlanType } from 'modules/billing/components/FactorialBundles/types'
import {
  ChargebeeBundle,
  getBundlePrice,
  getChargebeeSubscriptionItems,
} from 'modules/billing/lib/prices'
import subscriptions from 'modules/billing/store/collections/subscriptions'
import employees from 'modules/employees/store/collections/employees'
import {
  PricePeriods,
  PricingBundleProvider,
} from 'modules/onboarding/components/Pricing/PricingBundleContext'

import BenefitsList from './_BenefitsList'
import ExtraAddons from './_ExtraAddons'
import PriceTag from './_PriceTag'
import { AddonSelected } from './types'

type Props = {
  config: ChargebeeBundle
  isYearly: boolean
  planType: ValidPlanType
  benefits: string[]
  selectedBundle: string
}

const PricingBox = ({ planType, config, isYearly, benefits, selectedBundle }: Props) => {
  const currentSubscription = subscriptions.current

  const [addonsSelected, setAddonsSelected] = useState<AddonSelected[]>([])

  const filterAddonsBySelected = (addonConfig: ChargebeeBundle) => {
    return (
      addonConfig.extraAddons?.filter((addon) =>
        addonsSelected.some((sel) => sel.parentModule === addon.parentModule)
      ) || []
    )
  }

  const extraAddons = filterAddonsBySelected(config)

  const seats = employees.active.length

  const bundleSubscriptionItems = getChargebeeSubscriptionItems({
    ...config,
    annually: isYearly,
    seats,
    extraAddons,
  })

  const hasNoPlan = currentSubscription.inFreeTrial
  const FALLBACK_CURRENCY = 'EUR'
  const currency = bundleSubscriptionItems[0]?.plan.currencyCode || FALLBACK_CURRENCY
  const {
    recurringPrice: yearlyPrice,
    perSeat: yearlyPerSeat,
    oneTimePrice: yearlyOneTime,
  } = getBundlePrice({
    ...config,
    annually: true,
    seats: hasNoPlan ? config.minimumSeats : seats,
    extraAddons,
  })
  const {
    recurringPrice: monthlyPrice,
    perSeat: monthlyPerSeat,
    oneTimePrice: monthlyOneTime,
  } = getBundlePrice({
    ...config,
    annually: false,
    seats: hasNoPlan ? config.minimumSeats : seats,
    extraAddons,
  })
  const currentPlanPricing: PricePeriods = {
    yearly: {
      totalPrice: yearlyPrice,
      perSeatPrice: yearlyPerSeat,
      fixedFeePrice: yearlyOneTime,
    },
    monthly: {
      totalPrice: monthlyPrice,
      perSeatPrice: monthlyPerSeat,
      fixedFeePrice: monthlyOneTime,
    },
  }

  useEffect(() => {
    setAddonsSelected([])
  }, [config.corePlan.title])

  return (
    <PricingBundleProvider
      currentPlanPricing={currentPlanPricing}
      selectedBundle={selectedBundle}
      isYearly={isYearly}
      extraAddons={extraAddons}
      employeeSeats={config.minimumSeats}
      planType={planType}
      currency={currency}
    >
      <Box
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
        borderRadius={{ all: 'abs012' }}
        alignItems="center"
        minWidth="s240"
        style={{ position: 'relative', flex: '1', overflow: 'hidden' }}
      >
        <PriceTag
          isYearly={isYearly}
          seats={seats}
          perSeatPrice={isYearly ? yearlyPerSeat / 12 : monthlyPerSeat}
          title={config.title}
          totalPrice={isYearly ? yearlyPrice : monthlyPrice}
          description={config.description}
          minimumSeats={config.minimumSeats}
          currency={currency}
          bundleSubscriptionItems={bundleSubscriptionItems}
        />
        <Divider />
        <ExtraAddons
          currency={currency}
          availableAddons={config.extraAddons}
          addonsSelected={addonsSelected}
          setAddonsSelected={setAddonsSelected}
        />
        <Divider />
        <Box paddingX="s24" paddingY="s32" alignSelf="start">
          <Text size="300" color="grey800" weight="semibold">
            {planType === 'business'
              ? i18n.t('pricing_plans.hubs.features.business')
              : i18n.t('pricing_plans.hubs.features.enterprise')}
            {':'}
          </Text>
          <BenefitsList benefits={benefits} />
        </Box>
      </Box>
    </PricingBundleProvider>
  )
}
export default PricingBox
